import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Loader2, ShieldAlert, Download, Filter, TrendingUp } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/admin/resale")({
  head: () => ({ meta: [{ title: "Admin · Reventa — ZYNTRA" }] }),
  component: AdminResale,
});

type Row = {
  id: string;
  created_at: string;
  completed_at: string | null;
  status: string;
  sale_price: number;
  platform_fee_amount: number;
  payment_fee_amount: number;
  seller_net_amount: number;
  currency: string;
  ticket_id: string;
  event_id: string;
  buyer_id: string;
  seller_id: string;
  events?: { title: string | null; date: string | null } | null;
  buyer?: { display_name: string | null; email?: string | null } | null;
  seller?: { display_name: string | null; email?: string | null } | null;
};

function fmt(n: number, currency = "CRC") {
  return `${currency} ${Number(n || 0).toLocaleString("es-CR", { maximumFractionDigits: 0 })}`;
}

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-CR", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

function AdminResale() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<Row[]>([]);
  const [events, setEvents] = useState<{ id: string; title: string }[]>([]);
  const [profilesById, setProfilesById] = useState<Map<string, { display_name: string | null }>>(new Map());

  // Filters
  const [eventId, setEventId] = useState<string>("");
  const [status, setStatus] = useState<string>("completed");
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate({ to: "/login" }); return; }
    (async () => {
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();
      setIsAdmin(!!data);
      if (!data) setLoading(false);
    })();
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (isAdmin) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, eventId, status, from, to]);

  async function loadData() {
    setLoading(true);
    let q = supabase
      .from("resale_transactions")
      .select("*, events(title, date)")
      .order("created_at", { ascending: false })
      .limit(1000);
    if (status) q = q.eq("status", status);
    if (eventId) q = q.eq("event_id", eventId);
    if (from) q = q.gte("created_at", new Date(from).toISOString());
    if (to) {
      const end = new Date(to);
      end.setHours(23, 59, 59, 999);
      q = q.lte("created_at", end.toISOString());
    }

    const { data, error } = await q;
    if (error) {
      console.error("[admin-resale] load failed", error);
      setRows([]);
      setLoading(false);
      return;
    }
    const list = (data as any[]) || [];
    setRows(list as Row[]);

    // Distinct events for filter
    const distinct = new Map<string, string>();
    list.forEach((r) => {
      if (r.event_id && r.events?.title) distinct.set(r.event_id, r.events.title);
    });
    if (events.length === 0 && distinct.size > 0) {
      setEvents(Array.from(distinct, ([id, title]) => ({ id, title })));
    }

    // Profiles for buyer/seller display
    const userIds = Array.from(new Set(list.flatMap((r) => [r.buyer_id, r.seller_id]).filter(Boolean)));
    if (userIds.length > 0) {
      const { data: profs } = await supabase
        .from("profiles")
        .select("user_id, display_name")
        .in("user_id", userIds);
      const map = new Map<string, { display_name: string | null }>();
      (profs || []).forEach((p: any) => map.set(p.user_id, { display_name: p.display_name }));
      setProfilesById(map);
    }
    setLoading(false);
  }

  const totals = useMemo(() => {
    const t = { count: rows.length, sales: 0, platform: 0, payment: 0, net: 0 };
    rows.forEach((r) => {
      t.sales += Number(r.sale_price || 0);
      t.platform += Number(r.platform_fee_amount || 0);
      t.payment += Number(r.payment_fee_amount || 0);
      t.net += Number(r.seller_net_amount || 0);
    });
    return t;
  }, [rows]);

  const [granularity, setGranularity] = useState<"day" | "week" | "month">("day");

  const chartData = useMemo(() => {
    const buckets = new Map<string, { key: string; label: string; sortKey: string; platform: number; sales: number; count: number }>();
    const completed = rows.filter((r) => r.status === "completed");
    completed.forEach((r) => {
      const d = new Date(r.completed_at || r.created_at);
      let key: string;
      let label: string;
      let sortKey: string;
      if (granularity === "day") {
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        sortKey = key;
        label = d.toLocaleDateString("es-CR", { day: "2-digit", month: "short" });
      } else if (granularity === "week") {
        // ISO week-ish: snap to Monday
        const tmp = new Date(d);
        const day = (tmp.getDay() + 6) % 7;
        tmp.setDate(tmp.getDate() - day);
        key = `${tmp.getFullYear()}-W${String(tmp.getMonth() + 1).padStart(2, "0")}-${String(tmp.getDate()).padStart(2, "0")}`;
        sortKey = `${tmp.getFullYear()}-${String(tmp.getMonth() + 1).padStart(2, "0")}-${String(tmp.getDate()).padStart(2, "0")}`;
        label = `Sem ${tmp.toLocaleDateString("es-CR", { day: "2-digit", month: "short" })}`;
      } else {
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        sortKey = key;
        label = d.toLocaleDateString("es-CR", { month: "short", year: "2-digit" });
      }
      const cur = buckets.get(key) || { key, label, sortKey, platform: 0, sales: 0, count: 0 };
      cur.platform += Number(r.platform_fee_amount || 0);
      cur.sales += Number(r.sale_price || 0);
      cur.count += 1;
      buckets.set(key, cur);
    });
    return Array.from(buckets.values()).sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }, [rows, granularity]);

  function exportCsv() {
    const header = [
      "id", "created_at", "completed_at", "status",
      "event_title", "ticket_id",
      "seller_id", "seller_name", "buyer_id", "buyer_name",
      "sale_price", "platform_fee", "payment_fee", "seller_net", "currency",
    ];
    const lines = rows.map((r) => {
      const sellerName = profilesById.get(r.seller_id)?.display_name || "";
      const buyerName = profilesById.get(r.buyer_id)?.display_name || "";
      const cells = [
        r.id, r.created_at, r.completed_at || "", r.status,
        (r.events?.title || "").replace(/"/g, '""'),
        r.ticket_id, r.seller_id, sellerName, r.buyer_id, buyerName,
        r.sale_price, r.platform_fee_amount, r.payment_fee_amount, r.seller_net_amount, r.currency,
      ];
      return cells.map((c) => `"${String(c ?? "")}"`).join(",");
    });
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reventa-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  if (authLoading || isAdmin === null) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-md px-4 py-12 text-center">
        <ShieldAlert className="mx-auto h-10 w-10 text-destructive" />
        <h2 className="mt-3 text-xl font-bold text-foreground">Acceso denegado</h2>
        <Link to="/" className="mt-3 inline-block text-sm text-primary hover:underline">Volver al inicio</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 pb-20 pt-6 sm:px-6">
      <Link to="/dashboard/admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-4 w-4" /> Admin
      </Link>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-black text-foreground sm:text-3xl">Transacciones de reventa</h1>
          <p className="mt-1 text-sm text-muted-foreground">Filtrá, analizá y exportá la actividad de reventa</p>
        </div>
        <button
          onClick={exportCsv}
          disabled={rows.length === 0}
          className="inline-flex items-center gap-2 rounded-xl gradient-primary px-4 py-2 text-sm font-bold text-primary-foreground disabled:opacity-50"
        >
          <Download className="h-4 w-4" />
          Exportar CSV
        </button>
      </div>

      {/* Summary cards */}
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard label="Transacciones" value={String(totals.count)} />
        <SummaryCard label="Volumen vendido" value={fmt(totals.sales)} />
        <SummaryCard
          label="Ganancia plataforma"
          value={fmt(totals.platform)}
          highlight
          icon={<TrendingUp className="h-4 w-4" />}
        />
        <SummaryCard label="Pagado a vendedores" value={fmt(totals.net)} />
      </div>

      {/* Chart */}
      <div className="mt-6 rounded-2xl border border-border bg-card p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div>
            <h2 className="text-sm font-bold text-foreground">Ganancia neta de la plataforma</h2>
            <p className="text-xs text-muted-foreground">Solo transacciones completadas</p>
          </div>
          <div className="inline-flex rounded-lg border border-border bg-secondary p-0.5">
            {(["day", "week", "month"] as const).map((g) => (
              <button
                key={g}
                onClick={() => setGranularity(g)}
                className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                  granularity === g ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {g === "day" ? "Día" : g === "week" ? "Semana" : "Mes"}
              </button>
            ))}
          </div>
        </div>
        {chartData.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            No hay datos completados para graficar.
          </div>
        ) : (
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={11}
                  tickFormatter={(v) => Number(v).toLocaleString("es-CR", { maximumFractionDigits: 0 })}
                />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  labelStyle={{ color: "hsl(var(--foreground))", fontWeight: 600 }}
                  formatter={(value: number, name: string) => [
                    fmt(value),
                    name === "platform" ? "Ganancia plataforma" : name === "sales" ? "Volumen" : name,
                  ]}
                />
                <Line
                  type="monotone"
                  dataKey="platform"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: "hsl(var(--primary))" }}
                  activeDot={{ r: 5 }}
                  name="platform"
                />
                <Line
                  type="monotone"
                  dataKey="sales"
                  stroke="hsl(var(--muted-foreground))"
                  strokeWidth={1.5}
                  strokeDasharray="4 4"
                  dot={false}
                  name="sales"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="mt-6 rounded-2xl border border-border bg-card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-semibold text-foreground">Filtros</span>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="text-xs text-muted-foreground">Estado</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="">Todos</option>
              <option value="completed">Completadas</option>
              <option value="pending_payment">Pendientes</option>
              <option value="cancelled">Canceladas</option>
              <option value="refunded">Reembolsadas</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Evento</label>
            <select
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="">Todos</option>
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>{ev.title}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Desde</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Hasta</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>
        {(eventId || from || to || status !== "completed") && (
          <button
            onClick={() => { setEventId(""); setFrom(""); setTo(""); setStatus("completed"); }}
            className="mt-3 text-xs text-primary hover:underline"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Table */}
      <div className="mt-6 rounded-2xl border border-border bg-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No hay transacciones para los filtros aplicados.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2 text-left font-semibold">Fecha</th>
                  <th className="px-3 py-2 text-left font-semibold">Evento</th>
                  <th className="px-3 py-2 text-left font-semibold">Vendedor</th>
                  <th className="px-3 py-2 text-left font-semibold">Comprador</th>
                  <th className="px-3 py-2 text-right font-semibold">Venta</th>
                  <th className="px-3 py-2 text-right font-semibold">Plataforma</th>
                  <th className="px-3 py-2 text-right font-semibold">Pago</th>
                  <th className="px-3 py-2 text-right font-semibold">Neto vendedor</th>
                  <th className="px-3 py-2 text-left font-semibold">Estado</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-border/50 hover:bg-secondary/30">
                    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                      {fmtDate(r.completed_at || r.created_at)}
                    </td>
                    <td className="px-3 py-2 text-foreground">{r.events?.title || "—"}</td>
                    <td className="px-3 py-2 text-foreground">
                      {profilesById.get(r.seller_id)?.display_name || (
                        <span className="font-mono text-xs text-muted-foreground">{r.seller_id.slice(0, 8)}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-foreground">
                      {profilesById.get(r.buyer_id)?.display_name || (
                        <span className="font-mono text-xs text-muted-foreground">{r.buyer_id.slice(0, 8)}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold text-foreground">{fmt(r.sale_price, r.currency)}</td>
                    <td className="px-3 py-2 text-right text-success">{fmt(r.platform_fee_amount, r.currency)}</td>
                    <td className="px-3 py-2 text-right text-muted-foreground">{fmt(r.payment_fee_amount, r.currency)}</td>
                    <td className="px-3 py-2 text-right font-semibold text-foreground">{fmt(r.seller_net_amount, r.currency)}</td>
                    <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border bg-secondary/60 text-sm font-bold">
                  <td colSpan={4} className="px-3 py-2 text-right text-muted-foreground">Totales</td>
                  <td className="px-3 py-2 text-right text-foreground">{fmt(totals.sales)}</td>
                  <td className="px-3 py-2 text-right text-success">{fmt(totals.platform)}</td>
                  <td className="px-3 py-2 text-right text-muted-foreground">{fmt(totals.payment)}</td>
                  <td className="px-3 py-2 text-right text-foreground">{fmt(totals.net)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, highlight, icon }: { label: string; value: string; highlight?: boolean; icon?: React.ReactNode }) {
  return (
    <div className={`rounded-2xl border p-4 ${highlight ? "border-primary/40 bg-primary/5" : "border-border bg-card"}`}>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <div className={`mt-1 text-xl font-black ${highlight ? "gradient-text" : "text-foreground"}`}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    completed: "bg-success/20 text-success",
    pending_payment: "bg-warning/20 text-warning",
    cancelled: "bg-muted text-muted-foreground",
    refunded: "bg-destructive/20 text-destructive",
  };
  const labels: Record<string, string> = {
    completed: "Completada",
    pending_payment: "Pendiente",
    cancelled: "Cancelada",
    refunded: "Reembolsada",
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${styles[status] || styles.cancelled}`}>
      {labels[status] || status}
    </span>
  );
}
